(() => {
  if (window.DWCart) return;

  const drawer = document.getElementById('DWCartDrawer');
  if (!drawer) return;

  const state = {
    cart: null,
    busy: false,
    lastFocusedElement: null
  };

  const routesRoot =
    window.Shopify?.routes?.root ||
    document.documentElement.getAttribute('data-shopify-root') ||
    '/';

  const endpoint = (path) =>
    `${routesRoot.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

  const currency = drawer.dataset.currency || 'PLN';
  const freeShippingThreshold =
    Number(drawer.dataset.freeShippingThreshold) || 19900;

  const panel = drawer.querySelector('.dw-cart-drawer__panel');
  const itemsElement = drawer.querySelector('[data-dw-cart-items]');
  const emptyElement = drawer.querySelector('[data-dw-cart-empty]');
  const footerElement = drawer.querySelector('[data-dw-cart-footer]');
  const countElement = drawer.querySelector('[data-dw-cart-drawer-count]');
  const totalElement = drawer.querySelector('[data-dw-cart-total]');
  const shippingMessage = drawer.querySelector('[data-dw-shipping-message]');
  const shippingProgress = drawer.querySelector('[data-dw-shipping-progress]');

  const escapeHTML = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (cents) =>
    new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format((Number(cents) || 0) / 100);

  const sleep = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const requestJSON = async (path, options = {}) => {
    const response = await fetch(endpoint(path), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers || {})
      },
      ...options
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Nie udało się zaktualizować koszyka.'
      );
    }

    return data;
  };

  const fetchCart = async () => {
    await sleep(100);
    return requestJSON('cart.js', {
      method: 'GET',
      cache: 'no-store'
    });
  };

  const dispatchCartUpdate = (cart) => {
    document.dispatchEvent(
      new CustomEvent('shopify:cart:lines-update', {
        bubbles: true,
        detail: {
          cart,
          source: 'dw-cart'
        }
      })
    );

    document.dispatchEvent(
      new CustomEvent('dw:cart:update', {
        bubbles: true,
        detail: { cart }
      })
    );
  };

  const updateHeaderCount = (cart) => {
    const count = Number(cart.item_count) || 0;

    document
      .querySelectorAll('[data-dw-cart-count]')
      .forEach((element) => {
        element.textContent = count;
      });

    document
      .querySelectorAll('#header-group a[href*="/cart"]')
      .forEach((link) => {
        let counter = link.querySelector(
          [
            '[data-dw-cart-count]',
            '.dw-header__cart-count',
            '.cart-count-bubble',
            '[class*="cart-count"]',
            '[class*="cart__count"]'
          ].join(',')
        );

        if (!counter) {
          counter = Array.from(link.querySelectorAll('span')).find(
            (element) => /^\d+$/.test(element.textContent.trim())
          );
        }

        if (counter) {
          counter.textContent = count;
          return;
        }

        const walker = document.createTreeWalker(
          link,
          NodeFilter.SHOW_TEXT
        );

        let node;
        let replaced = false;

        while ((node = walker.nextNode())) {
          if (/Koszyk\s*\(\d+\)/i.test(node.nodeValue || '')) {
            node.nodeValue = node.nodeValue.replace(
              /Koszyk\s*\(\d+\)/i,
              `Koszyk (${count})`
            );
            replaced = true;
            break;
          }
        }

        if (!replaced) {
          const badge = document.createElement('span');
          badge.className = 'dw-js-cart-count';
          badge.dataset.dwCartCount = '';
          badge.textContent = count;
          link.append(badge);
        }
      });
  };

  const buildProperties = (properties) => {
    if (!properties || typeof properties !== 'object') return '';

    const rows = Object.entries(properties)
      .filter(([name, value]) => {
        return value && !String(name).startsWith('_');
      })
      .map(([name, value]) => {
        return `<span>${escapeHTML(name)}: ${escapeHTML(value)}</span>`;
      })
      .join('');

    return rows
      ? `<div class="dw-cart-drawer__item-properties">${rows}</div>`
      : '';
  };

  const buildItem = (item, index) => {
    const image = item.image
      ? `<img
          class="dw-cart-drawer__item-image"
          src="${escapeHTML(item.image)}"
          alt="${escapeHTML(item.product_title)}"
          loading="lazy"
        >`
      : `<span class="dw-cart-drawer__item-placeholder" aria-hidden="true">□</span>`;

    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<span class="dw-cart-drawer__item-variant">${escapeHTML(item.variant_title)}</span>`
        : '';

    const vendor = item.vendor
      ? `<span class="dw-cart-drawer__item-vendor">${escapeHTML(item.vendor)}</span>`
      : '';

    const price =
      item.final_line_price ??
      item.line_price ??
      (item.price * item.quantity);

    return `
      <article class="dw-cart-drawer__item" data-dw-cart-line="${index + 1}">
        <a class="dw-cart-drawer__item-image-wrap" href="${escapeHTML(item.url)}">
          ${image}
        </a>

        <div class="dw-cart-drawer__item-content">
          ${vendor}

          <a class="dw-cart-drawer__item-title" href="${escapeHTML(item.url)}">
            ${escapeHTML(item.product_title)}
          </a>

          ${variant}
          ${buildProperties(item.properties)}

          <div class="dw-cart-drawer__item-bottom">
            <div class="dw-cart-drawer__quantity">
              <button
                type="button"
                data-dw-cart-change
                data-line="${index + 1}"
                data-quantity="${Math.max(0, item.quantity - 1)}"
                aria-label="Zmniejsz ilość"
              >−</button>

              <span>${item.quantity}</span>

              <button
                type="button"
                data-dw-cart-change
                data-line="${index + 1}"
                data-quantity="${item.quantity + 1}"
                aria-label="Zwiększ ilość"
              >+</button>
            </div>

            <strong class="dw-cart-drawer__item-price">
              ${formatMoney(price)}
            </strong>
          </div>
        </div>

        <button
          class="dw-cart-drawer__remove"
          type="button"
          data-dw-cart-change
          data-line="${index + 1}"
          data-quantity="0"
          aria-label="Usuń ${escapeHTML(item.product_title)}"
        >×</button>
      </article>
    `;
  };

  const renderDrawer = (cart) => {
    state.cart = cart;

    const isEmpty = cart.item_count === 0;

    itemsElement.hidden = isEmpty;
    emptyElement.hidden = !isEmpty;
    footerElement.hidden = isEmpty;

    countElement.textContent = `(${cart.item_count})`;
    totalElement.textContent = formatMoney(cart.total_price);

    itemsElement.innerHTML = cart.items
      .map((item, index) => buildItem(item, index))
      .join('');

    const remaining = Math.max(
      0,
      freeShippingThreshold - cart.total_price
    );

    shippingMessage.textContent =
      remaining === 0
        ? 'Masz darmową dostawę!'
        : `Do darmowej dostawy brakuje ${formatMoney(remaining)}.`;

    shippingProgress.style.width =
      `${Math.min(
        100,
        (cart.total_price / freeShippingThreshold) * 100
      )}%`;

    updateHeaderCount(cart);
    dispatchCartUpdate(cart);
  };

  const getFocusableElements = () =>
    Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

  const openDrawer = async () => {
    state.lastFocusedElement = document.activeElement;

    try {
      renderDrawer(await fetchCart());
    } catch (error) {
      console.error(error);
    }

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('dw-cart-open');

    window.requestAnimationFrame(() => {
      panel.focus();
    });
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('dw-cart-open');

    if (state.lastFocusedElement instanceof HTMLElement) {
      state.lastFocusedElement.focus();
    }
  };

  const changeLine = async (line, quantity) => {
    if (state.busy) return;

    state.busy = true;
    drawer.classList.add('is-loading');

    try {
      const cart = await requestJSON('cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Math.max(0, Number(quantity))
        })
      });

      renderDrawer(cart);
    } catch (error) {
      console.error(error);
      window.alert(error.message);
    } finally {
      state.busy = false;
      drawer.classList.remove('is-loading');
    }
  };

  const addFormToCart = async (form, submitter) => {
    if (state.busy) return;

    state.busy = true;

    const label =
      submitter?.querySelector('[data-dw-add-label]') ||
      submitter?.querySelector('span');

    const originalLabel = label?.textContent;
    submitter?.setAttribute('disabled', 'disabled');

    if (label) label.textContent = 'Dodawanie...';

    try {
      await requestJSON('cart/add.js', {
        method: 'POST',
        body: new FormData(form)
      });

      const cart = await fetchCart();
      renderDrawer(cart);

      if (label) label.textContent = 'Dodano';

      await openDrawer();
    } catch (error) {
      console.error(error);

      const messageElement =
        form.querySelector('[data-dw-form-message]');

      if (messageElement) {
        messageElement.textContent = error.message;
      } else {
        window.alert(error.message);
      }

      if (label) label.textContent = originalLabel || 'Dodaj do koszyka';
    } finally {
      state.busy = false;

      window.setTimeout(() => {
        submitter?.removeAttribute('disabled');

        if (label && originalLabel) {
          label.textContent = originalLabel;
        }
      }, 900);
    }
  };

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-dw-cart-close]');

    if (closeButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const changeButton = event.target.closest('[data-dw-cart-change]');

    if (changeButton) {
      event.preventDefault();

      changeLine(
        changeButton.dataset.line,
        changeButton.dataset.quantity
      );

      return;
    }

    const explicitOpen = event.target.closest('[data-dw-cart-open]');

    const headerCartLink = event.target.closest(
      '#header-group a[href*="/cart"]'
    );

    if (explicitOpen || headerCartLink) {
      event.preventDefault();
      openDrawer();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('.dw-scope')) return;
    if (!/\/cart\/add(?:\.js)?(?:\?|$)/.test(form.action)) return;

    const submitter = event.submitter;

    if (
      submitter?.name &&
      submitter.name !== 'add'
    ) {
      return;
    }

    event.preventDefault();
    addFormToCart(form, submitter);
  });

  document.addEventListener('keydown', (event) => {
    if (!drawer.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();

    if (!focusable.length) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  });

  fetchCart()
    .then(renderDrawer)
    .catch((error) => console.error(error));

  window.DWCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: async () => renderDrawer(await fetchCart())
  };
})();
